import React, { useState } from 'react';
import { useDataProvider } from 'react-admin';
import classNames from 'classnames';

import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';

import { Dialog } from 'components/common';
import UpsAgreements from './UpsAgreements';
import useCustomNotify from 'hooks/useCustomNotify';
import { DialogTitle } from '@material-ui/core';
import { SUPPORTED_LANGUAGE } from 'services/helpers/constants';
import { getDomesticCountryCode } from 'services/helpers/countryCode';

const useStyles = makeStyles((theme) => ({
  titleContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  languageContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '4px',
  },
  language: {
    color: theme.palette.primary.main,
    padding: '0rem 0',
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  activeLanguage: {
    fontWeight: 900,
  },
  divider: {
    borderRight: `1px solid ${theme.palette.primary.main}`,
  },
}));

interface AgreementTitleProps {
  activeLanguage: string;
  setLanguage: (language: string) => void;
}

const AgreementTitle = ({ activeLanguage, setLanguage }: AgreementTitleProps) => {
  const classes = useStyles();

  const domesticCountryCode = getDomesticCountryCode();

  return (
    <Box className={classes.titleContainer}>
      <DialogTitle>
        Activate UPS<sup>&reg;</sup> Ground Saver
      </DialogTitle>
      {domesticCountryCode !== 'US' && (
        <Box className={classes.languageContainer}>
          {SUPPORTED_LANGUAGE.map((language, idx) => (
            <React.Fragment key={language}>
              <Typography
                onClick={() => setLanguage(language)}
                className={classNames(classes.language, {
                  [classes.activeLanguage]: language === activeLanguage,
                })}>
                {language}
              </Typography>
              {idx < SUPPORTED_LANGUAGE.length - 1 && (
                <Divider orientation="vertical" flexItem className={classes.divider} />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

const Note = () => (
  <Typography>
    UPS, the UPS brandmark, UPS Ready<sup>®</sup>, and the color brown are trademarks of United
    Parcel Service of America, Inc. All Rights Reserved.
  </Typography>
);

interface Props {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  upsId?: string;
}

const GroundSaverActivation = ({ open, onConfirm, onCancel, upsId }: Props) => {
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();

  const [language, setLanguage] = useState('en');
  const [submitting, setSubmitting] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const onUpsEnablingConfirm = async () => {
    try {
      setSubmitting(true);
      await dataProvider.put('upsAgreemetsById', { id: upsId, data: {} });

      setOpenConfirmationDialog(true);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while activating UPS Ground Saver';
      notify(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    setOpenConfirmationDialog(false);
    onConfirm();
  };
  return (
    <>
      <Dialog
        title="Congratulations!"
        open={!!openConfirmationDialog}
        onCancel={handleConfirm}
        submitting={submitting}
        cancelSize={12}
        cancelText="OK">
        <Typography>
          UPS<sup>&reg;</sup> Ground Saver has been successfully activated. You may now purchase
          your label
        </Typography>
      </Dialog>
      <Dialog
        title={<AgreementTitle activeLanguage={language} setLanguage={setLanguage} />}
        note={<Note />}
        open={!!open}
        confirmText="I Accept"
        onConfirm={onUpsEnablingConfirm}
        onCancel={onCancel}
        submitting={submitting}>
        <UpsAgreements language={language} />
      </Dialog>
    </>
  );
};
export default GroundSaverActivation;
